import React, { useState, useRef } from 'react';
import { Info, Plus, Check, X, AlertCircle, Sparkles, GripVertical, Edit, Trash2, Camera, Lightbulb, Home, MapPin, Maximize, Layers, DoorOpen, Bath, Upload, Link2 } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
import { Tabs } from '../components/ui/Tabs';
import { Card, CardContent } from '../components/ui/Card';
// Types
interface DesiderataItem {
  id: string;
  text: string;
  active: boolean;
}
interface PhotoItem {
  id: string;
  url: string;
  room: string;
}
export function SchedaProprietaPage() {
  // State: General Info
  const [generalInfo, setGeneralInfo] = useState({
    address: 'Via dei Giardini 12',
    city: 'Milano',
    floor: '3',
    sqm: '120',
    rooms: '4',
    bathrooms: '2'
  });
  // State: Materials
  const [materials, setMaterials] = useState<string[]>(['Parquet antico', 'Marmo Carrara', 'Tessuti delicati']);
  const [newMaterial, setNewMaterial] = useState('');
  const [materialNote, setMaterialNote] = useState('Il parquet in salotto è molto delicato, usare solo prodotti specifici e panno ben strizzato.');
  // State: Products
  const [favProducts, setFavProducts] = useState<string[]>(['Detergente Bio', 'Anticalcare X', 'Sgrassatore Limone']);
  const [avoidProducts, setAvoidProducts] = useState<string[]>(['Candeggina', 'Ammoniaca']);
  const [newFavProduct, setNewFavProduct] = useState('');
  const [newAvoidProduct, setNewAvoidProduct] = useState('');
  // State: Access
  const [accessInfo, setAccessInfo] = useState({
    type: 'codice',
    code: '1234 #',
    notes: 'Il tastierino si trova sulla sinistra del portone.'
  });
  // Tab State
  const [activeTab, setActiveTab] = useState('informazioni');
  // State: Storico Filter
  const [storicoFilter, setStoricoFilter] = useState<'tutti' | 'pulizie' | 'straordinari'>('tutti');
  // Mock data: Storico interventions
  const storicoData = [{
    id: '1',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '15 Gen 2024',
    duration: '3h 45min',
    status: 'Completato'
  }, {
    id: '2',
    type: 'straordinario',
    icon: '⚡',
    title: 'Lampadina bagno',
    date: '15 Gen 2024',
    duration: '30min',
    status: 'Completato'
  }, {
    id: '3',
    type: 'pulizia',
    icon: '🧹',
    title: 'Deep cleaning',
    date: '10 Gen 2024',
    duration: '5h',
    status: 'Completato'
  }, {
    id: '4',
    type: 'straordinario',
    icon: '⚡',
    title: 'Riparazione rubinetto',
    date: '8 Gen 2024',
    duration: '1h 15min',
    status: 'Completato'
  }, {
    id: '5',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '5 Gen 2024',
    duration: '3h 30min',
    status: 'Completato'
  }];
  // Filter storico data based on selected filter
  const filteredStorico = storicoData.filter(item => {
    if (storicoFilter === 'tutti') return true;
    if (storicoFilter === 'pulizie') return item.type === 'pulizia';
    if (storicoFilter === 'straordinari') return item.type === 'straordinario';
    return true;
  });
  // State: Desiderata
  const [desiderata, setDesiderata] = useState<DesiderataItem[]>([{
    id: '1',
    text: 'Lenzuola sempre stirate',
    active: true
  }, {
    id: '2',
    text: 'Cuscini divano sistemati in modo specifico',
    active: true
  }, {
    id: '3',
    text: 'Asciugamani piegati a cigno',
    active: false
  }, {
    id: '4',
    text: 'Lasciare luci ingresso accese',
    active: true
  }]);
  const [newDesiderata, setNewDesiderata] = useState('');
  // State: Photos
  const [photos, setPhotos] = useState<PhotoItem[]>([{
    id: '1',
    url: 'https://cdn.magicpatterns.com/uploads/dVhw2cGTmUhE4Ap2uCd1sm/_livingroom_clean.png',
    room: 'Soggiorno'
  }, {
    id: '2',
    url: 'https://cdn.magicpatterns.com/uploads/q7HEF8xL4ZfPHBQejbrvyX/_kitchen_clean.png',
    room: 'Cucina'
  }, {
    id: '3',
    url: 'https://cdn.magicpatterns.com/uploads/9cNyWn1us7ZSVXzYMKkr7V/_bathroom_clean.png',
    room: 'Bagno'
  }, {
    id: '4',
    url: 'https://cdn.magicpatterns.com/uploads/vL5vGhBPD2XELKb3m3514D/_bedroom_clean.png',
    room: 'Camera'
  }]);
  // State: Add Photo Modal
  const [photoModalRoom, setPhotoModalRoom] = useState<string | null>(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [photoSourceTab, setPhotoSourceTab] = useState<'upload' | 'url'>('upload');
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  // State: Add New Room Modal
  const [newRoomModalOpen, setNewRoomModalOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomPhotoUrl, setNewRoomPhotoUrl] = useState('');
  const [roomSourceTab, setRoomSourceTab] = useState<'upload' | 'url'>('upload');
  const [isDraggingRoom, setIsDraggingRoom] = useState(false);
  const roomFileInputRef = useRef<HTMLInputElement>(null);
  // File handling helpers
  const readFileAsDataUrl = (file: File, onLoad: (dataUrl: string) => void) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result;
      if (typeof result === 'string') onLoad(result);
    };
    reader.readAsDataURL(file);
  };
  // Preset photo suggestions for both modals
  const presetPhotos = ['https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=400'];
  const closePhotoModal = () => {
    setPhotoModalRoom(null);
    setNewPhotoUrl('');
    setPhotoSourceTab('upload');
    setIsDraggingPhoto(false);
  };
  const handleAddPhoto = () => {
    if (!photoModalRoom || !newPhotoUrl.trim()) return;
    setPhotos([...photos, {
      id: Date.now().toString(),
      url: newPhotoUrl.trim(),
      room: photoModalRoom
    }]);
    closePhotoModal();
  };
  const closeRoomModal = () => {
    setNewRoomModalOpen(false);
    setNewRoomName('');
    setNewRoomPhotoUrl('');
    setRoomSourceTab('upload');
    setIsDraggingRoom(false);
  };
  const handleAddRoom = () => {
    if (!newRoomName.trim()) return;
    const roomName = newRoomName.trim();
    const photoUrl = newRoomPhotoUrl.trim() || presetPhotos[0];
    setPhotos([...photos, {
      id: Date.now().toString(),
      url: photoUrl,
      room: roomName
    }]);
    closeRoomModal();
  };
  // Handlers
  const handleAddMaterial = () => {
    if (newMaterial.trim()) {
      setMaterials([...materials, newMaterial.trim()]);
      setNewMaterial('');
    }
  };
  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleAddFavProduct = () => {
    if (newFavProduct.trim()) {
      setFavProducts([...favProducts, newFavProduct.trim()]);
      setNewFavProduct('');
    }
  };
  const handleAddAvoidProduct = () => {
    if (newAvoidProduct.trim()) {
      setAvoidProducts([...avoidProducts, newAvoidProduct.trim()]);
      setNewAvoidProduct('');
    }
  };
  const toggleDesiderata = (id: string) => {
    setDesiderata(desiderata.map(item => item.id === id ? {
      ...item,
      active: !item.active
    } : item));
  };
  const deleteDesiderata = (id: string) => {
    setDesiderata(desiderata.filter(item => item.id !== id));
  };
  const addDesiderata = () => {
    if (newDesiderata.trim()) {
      setDesiderata([...desiderata, {
        id: Date.now().toString(),
        text: newDesiderata.trim(),
        active: true
      }]);
      setNewDesiderata('');
    }
  };
  const addAISuggestion = () => {
    setDesiderata([...desiderata, {
      id: Date.now().toString(),
      text: 'Attenzione al parquet zona ingresso',
      active: true
    }]);
  };
  return <MobileLayout data-id="element-4118">
      {/* Page Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8" data-id="element-4119">
        <div data-id="element-4120">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-4121">
            Scheda Proprietà
          </h1>
        </div>
        <button className="w-full sm:w-auto px-4 py-2.5 bg-coral text-white rounded-xl hover:bg-coral-600 transition-colors text-sm min-h-[44px]" data-id="element-4122">
          Salva Modifiche
        </button>
      </div>

      {/* Tab Navigation - Pill style (same as Pulizie) */}
      <Tabs className="mb-6 -mx-4 px-4 lg:mx-0 lg:px-0" scrollable value={activeTab} onValueChange={setActiveTab} items={[{
      id: 'informazioni',
      label: 'Informazioni'
    }, {
      id: 'accesso',
      label: 'Accesso'
    }, {
      id: 'preferenze',
      label: 'Preferenze'
    }, {
      id: 'attenzione',
      label: 'Punti di Attenzione'
    }]} data-id="element-4123" />

      {/* Info Generali Card - Tab: Informazioni */}
      {activeTab === 'informazioni' && <>
          <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-4124">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-4125">
              <div className="mb-4 lg:mb-6" data-id="element-4126">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4127">
                  <Home size={18} data-id="element-4128" />
                  Info Generali
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-4129">
                <div data-id="element-4130">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4131">
                    Indirizzo
                  </label>
                  <div className="relative" data-id="element-4132">
                    <MapPin className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-4133" />
                    <input type="text" value={generalInfo.address} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  address: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4134" />
                  </div>
                </div>
                <div data-id="element-4135">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4136">
                    Città
                  </label>
                  <input type="text" value={generalInfo.city} onChange={e => setGeneralInfo({
                ...generalInfo,
                city: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4137" />
                </div>
                <div data-id="element-4138">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4139">
                    Piano
                  </label>
                  <div className="relative" data-id="element-4140">
                    <Layers className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-4141" />
                    <input type="text" value={generalInfo.floor} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  floor: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4142" />
                  </div>
                </div>
                <div data-id="element-4143">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4144">
                    Mq
                  </label>
                  <div className="relative" data-id="element-4145">
                    <Maximize className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-4146" />
                    <input type="text" value={generalInfo.sqm} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  sqm: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4147" />
                  </div>
                </div>
                <div data-id="element-4148">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4149">
                    Numero Stanze
                  </label>
                  <div className="relative" data-id="element-4150">
                    <DoorOpen className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-4151" />
                    <input type="text" value={generalInfo.rooms} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  rooms: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4152" />
                  </div>
                </div>
                <div data-id="element-4153">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4154">
                    Numero Bagni
                  </label>
                  <div className="relative" data-id="element-4155">
                    <Bath className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-4156" />
                    <input type="text" value={generalInfo.bathrooms} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  bathrooms: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-4157" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Foto Riferimento Card */}
          <Card className="shadow-sm" data-id="element-4158">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-4159">
              <div className="mb-4 lg:mb-6" data-id="element-4160">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4161">
                  <Camera size={18} data-id="element-4162" />
                  Foto Riferimento
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-4163">
                  Queste foto verranno usate dall'AI per confrontare il
                  prima/dopo
                </p>
              </div>

              <div className="space-y-4 lg:space-y-6" data-id="element-4164">
                {Array.from(new Set(photos.map(p => p.room))).map(roomName => {
              const roomPhotos = photos.filter(p => p.room === roomName);
              return <div key={roomName} className="space-y-3" data-id="element-4165">
                        <div className="flex items-center justify-between" data-id="element-4166">
                          <h3 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-4167">
                            <DoorOpen size={16} className="text-teal-deep" data-id="element-4168" />
                            {roomName}
                          </h3>
                          <span className="text-xs text-gray-500" data-id="element-4169">
                            {roomPhotos.length} foto
                          </span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3 p-3 lg:p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4170">
                          {roomPhotos.map(photo => <div key={photo.id} className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-id="element-4171">
                              <img src={photo.url} alt={photo.room} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-4172" />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-start justify-end p-2" data-id="element-4173">
                                <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex gap-1" data-id="element-4174">
                                  <button className="p-1.5 bg-white text-gray-700 rounded-full hover:text-navy shadow-sm" data-id="element-4175">
                                    <Edit size={12} data-id="element-4176" />
                                  </button>
                                  <button onClick={() => setPhotos(photos.filter(p => p.id !== photo.id))} className="p-1.5 bg-white text-red-500 rounded-full hover:text-red-700 shadow-sm" data-id="element-4177">
                                    <Trash2 size={12} data-id="element-4178" />
                                  </button>
                                </div>
                              </div>
                            </div>)}

                          <button onClick={() => setPhotoModalRoom(roomName)} className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-teal-deep hover:bg-white transition-all flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-teal-deep min-h-[44px]" data-id="element-4179">
                            <Plus size={20} data-id="element-4180" />
                            <span className="text-xs font-medium" data-id="element-4181">
                              Aggiungi
                            </span>
                          </button>
                        </div>
                      </div>;
            })}

                <button onClick={() => setNewRoomModalOpen(true)} className="w-full p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-mint-light transition-all flex items-center justify-center gap-2 text-gray-500 hover:text-teal-deep min-h-[44px]" data-id="element-4182">
                  <Plus size={18} data-id="element-4183" />
                  <span className="text-sm font-medium" data-id="element-4184">
                    Aggiungi nuova stanza
                  </span>
                </button>
              </div>
            </CardContent>
          </Card>
        </>}

      {/* Punti di Attenzione Tab */}
      {activeTab === 'attenzione' && <Card className="shadow-sm" data-id="element-4185">
          <CardContent noPadding className="p-4 lg:p-6" data-id="element-4186">
            <div className="mb-4 lg:mb-6" data-id="element-4187">
              <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4188">
                <Info size={18} data-id="element-4189" />
                Materiali & Attenzioni
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-4190">
                Indica i materiali presenti che richiedono attenzione
                particolare
              </p>
            </div>

            <div className="space-y-4" data-id="element-4191">
              <div className="flex flex-wrap gap-2" data-id="element-4192">
                {materials.map((material, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-4193">
                    {material}
                    <button onClick={() => removeMaterial(index)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-4194">
                      <X size={14} data-id="element-4195" />
                    </button>
                  </span>)}
              </div>

              <div className="flex gap-2" data-id="element-4196">
                <input type="text" value={newMaterial} onChange={e => setNewMaterial(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddMaterial()} placeholder="Aggiungi materiale..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-4197" />
                <button onClick={handleAddMaterial} disabled={!newMaterial.trim()} className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-4198">
                  <Plus size={18} data-id="element-4199" />
                </button>
              </div>

              <div data-id="element-4200">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4201">
                  Note aggiuntive sui materiali
                </label>
                <textarea value={materialNote} onChange={e => setMaterialNote(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all resize-none" placeholder="Inserisci dettagli specifici su come trattare le superfici..." data-id="element-4202" />
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Preferenze Pulizia Tab */}
      {activeTab === 'preferenze' && <>
          <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-4203">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-4204">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-4205">
                {/* Preferred Products */}
                <div data-id="element-4206">
                  <div className="mb-4" data-id="element-4207">
                    <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4208">
                      <div className="p-1 bg-green-100 rounded-full text-green-600" data-id="element-4209">
                        <Check size={14} data-id="element-4210" />
                      </div>
                      Prodotti Preferiti
                    </h3>
                  </div>

                  <div data-id="element-4211">
                    <div className="flex flex-wrap gap-2 mb-3" data-id="element-4212">
                      {favProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-4213">
                          {product}
                          <button onClick={() => setFavProducts(favProducts.filter((_, i) => i !== index))} className="ml-2 text-green-500 hover:text-green-700" data-id="element-4214">
                            <X size={14} data-id="element-4215" />
                          </button>
                        </span>)}
                    </div>
                    <div className="flex gap-2" data-id="element-4216">
                      <input type="text" value={newFavProduct} onChange={e => setNewFavProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddFavProduct()} placeholder="Nuovo prodotto..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-4217" />
                      <button onClick={handleAddFavProduct} disabled={!newFavProduct.trim()} className="px-3 py-2.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-4218">
                        <Plus size={18} data-id="element-4219" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Avoided Products */}
                <div data-id="element-4220">
                  <div className="mb-4" data-id="element-4221">
                    <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4222">
                      <div className="p-1 bg-red-100 rounded-full text-red-600" data-id="element-4223">
                        <X size={14} data-id="element-4224" />
                      </div>
                      Prodotti da Evitare
                    </h3>
                  </div>

                  <div data-id="element-4225">
                    <div className="flex flex-wrap gap-2 mb-3" data-id="element-4226">
                      {avoidProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100" data-id="element-4227">
                          {product}
                          <button onClick={() => setAvoidProducts(avoidProducts.filter((_, i) => i !== index))} className="ml-2 text-red-500 hover:text-red-700" data-id="element-4228">
                            <X size={14} data-id="element-4229" />
                          </button>
                        </span>)}
                    </div>
                    <div className="flex gap-2" data-id="element-4230">
                      <input type="text" value={newAvoidProduct} onChange={e => setNewAvoidProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddAvoidProduct()} placeholder="Prodotto da evitare..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-4231" />
                      <button onClick={handleAddAvoidProduct} disabled={!newAvoidProduct.trim()} className="px-3 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-4232">
                        <Plus size={18} data-id="element-4233" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Desiderata Card */}
          <Card className="shadow-sm" data-id="element-4234">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-4235">
              <div className="mb-4 lg:mb-6" data-id="element-4236">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-4237">
                  <Sparkles size={18} className="text-coral" data-id="element-4238" />
                  Desiderata Pulizia
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-4239">
                  Questi elementi verranno aggiunti automaticamente alla
                  checklist del fornitore
                </p>
              </div>

              <div className="space-y-3 mb-6" data-id="element-4240">
                {desiderata.map(item => <div key={item.id} className="group flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-all" data-id="element-4241">
                    <div className="cursor-grab text-gray-400 hover:text-gray-600 hidden sm:block" data-id="element-4242">
                      <GripVertical size={18} data-id="element-4243" />
                    </div>
                    <button onClick={() => toggleDesiderata(item.id)} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-deep focus:ring-offset-2 flex-shrink-0 ${item.active ? 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-4244">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.active ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-4245" />
                    </button>
                    <span className={`flex-1 text-sm ${item.active ? 'text-gray-700' : 'text-gray-400 line-through'}`} data-id="element-4246">
                      {item.text}
                    </span>
                    <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-4247">
                      <button className="p-1.5 text-gray-400 hover:text-navy hover:bg-white rounded-md" data-id="element-4248">
                        <Edit size={14} data-id="element-4249" />
                      </button>
                      <button onClick={() => deleteDesiderata(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-md" data-id="element-4250">
                        <Trash2 size={14} data-id="element-4251" />
                      </button>
                    </div>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-4252">
                <input type="text" value={newDesiderata} onChange={e => setNewDesiderata(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDesiderata()} placeholder="Aggiungi una nuova richiesta specifica..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-4253" />
                <button onClick={addDesiderata} disabled={!newDesiderata.trim()} className="px-4 py-2.5 bg-coral text-white rounded-xl hover:bg-coral-600 disabled:opacity-50 font-medium text-sm min-h-[44px] whitespace-nowrap" data-id="element-4254">
                  Aggiungi
                </button>
              </div>

              <div className="pt-6 border-t border-gray-100" data-id="element-4255">
                <div className="bg-coral/5 border border-coral/20 rounded-lg p-4 flex items-start gap-3" data-id="element-4256">
                  <div className="p-2 bg-white rounded-full text-yellow-500 shadow-sm flex-shrink-0" data-id="element-4257">
                    <Lightbulb size={18} data-id="element-4258" />
                  </div>
                  <div className="flex-1" data-id="element-4259">
                    <p className="text-sm text-coral font-medium mb-1" data-id="element-4260">
                      Suggerimento AI
                    </p>
                    <p className="text-xs text-coral mb-3" data-id="element-4261">
                      In base alle segnalazioni precedenti, considera di
                      aggiungere "Attenzione al parquet zona ingresso"
                    </p>
                    <button onClick={addAISuggestion} className="text-xs font-medium text-coral hover:text-coral-600 underline" data-id="element-4262">
                      Aggiungi ai desiderata
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>}

      {/* Accesso Tab */}
      {activeTab === 'accesso' && <Card className="shadow-sm" data-id="element-4263">
          <CardContent noPadding className="p-4 lg:p-6" data-id="element-4264">
            <div className="mb-4 lg:mb-6" data-id="element-4265">
              <h2 className="text-sm font-medium text-navy" data-id="element-4266">
                Istruzioni Accesso
              </h2>
            </div>

            <div className="bg-coral/5 border-l-4 border-coral p-4 lg:p-6 rounded-r-lg mb-6" data-id="element-4267">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-4268">
                <div data-id="element-4269">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4270">
                    Tipo Accesso
                  </label>
                  <select value={accessInfo.type} onChange={e => setAccessInfo({
                ...accessInfo,
                type: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-4271">
                    <option value="chiavi" data-id="element-4272">Chiavi fisiche</option>
                    <option value="codice" data-id="element-4273">Codice tastierino</option>
                    <option value="portineria" data-id="element-4274">Portineria</option>
                    <option value="smart" data-id="element-4275">Smart Lock</option>
                    <option value="vickey" data-id="element-4276">Vickey</option>
                  </select>
                </div>
                <div data-id="element-4277">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4278">
                    Codice / Info Chiavi
                  </label>
                  <input type="text" value={accessInfo.code} onChange={e => setAccessInfo({
                ...accessInfo,
                code: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none min-h-[44px]" data-id="element-4279" />
                </div>
                <div className="sm:col-span-2" data-id="element-4280">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4281">
                    Note Accesso
                  </label>
                  <textarea value={accessInfo.notes} onChange={e => setAccessInfo({
                ...accessInfo,
                notes: e.target.value
              })} rows={2} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none resize-none" data-id="element-4282" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-4283">
              <div className="flex items-start gap-2 text-xs text-coral font-medium" data-id="element-4284">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-4285" />
                <span data-id="element-4286">
                  Queste informazioni saranno visibili al fornitore solo dopo
                  conferma intervento
                </span>
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Storico Tab */}
      {activeTab === 'storico' && <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-4287">
          <div className="mb-4 lg:mb-6" data-id="element-4288">
            <h2 className="text-sm font-medium text-navy" data-id="element-4289">
              Storico Interventi
            </h2>
            <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-4290">
              Cronologia di tutti gli interventi effettuati
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 mb-4" data-id="element-4291">
            <button onClick={() => setStoricoFilter('tutti')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'tutti' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4292">
              Tutti
            </button>
            <button onClick={() => setStoricoFilter('pulizie')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'pulizie' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4293">
              Pulizie
            </button>
            <button onClick={() => setStoricoFilter('straordinari')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'straordinari' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4294">
              Straordinari
            </button>
          </div>

          {/* History List */}
          <div className="space-y-3" data-id="element-4295">
            {filteredStorico.map(item => <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-4296">
                <div className="flex items-center gap-3" data-id="element-4297">
                  <span className="text-lg" data-id="element-4298">{item.icon}</span>
                  <div data-id="element-4299">
                    <div className="text-sm font-medium text-navy" data-id="element-4300">
                      {item.title}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-4301">{item.date}</div>
                  </div>
                </div>
                <div className="text-right" data-id="element-4302">
                  <div className="text-sm text-gray-600" data-id="element-4303">{item.duration}</div>
                  <div className="text-xs text-green-600" data-id="element-4304">✓ {item.status}</div>
                </div>
              </div>)}

            {filteredStorico.length === 0 && <div className="text-center py-8 text-gray-400 text-sm" data-id="element-4305">
                Nessun intervento trovato
              </div>}
          </div>
        </div>}

      {/* Add Photo Modal */}
      {photoModalRoom && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closePhotoModal} data-id="element-4306">
          <div className="bg-white rounded-2xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-4307">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-4308">
              <div data-id="element-4309">
                <h2 className="text-base font-bold text-navy" data-id="element-4310">Aggiungi foto</h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-4311">
                  Stanza:{' '}
                  <span className="font-medium text-foreground" data-id="element-4312">
                    {photoModalRoom}
                  </span>
                </p>
              </div>
              <button onClick={closePhotoModal} className="p-1.5 hover:bg-muted rounded-full transition-colors text-muted-foreground" aria-label="Chiudi" data-id="element-4313">
                <X size={18} data-id="element-4314" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4" data-id="element-4315">
              {/* Source tabs: Upload | URL */}
              <div className="flex gap-1 p-1 bg-muted rounded-lg" data-id="element-4316">
                <button onClick={() => setPhotoSourceTab('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${photoSourceTab === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-4317">
                  <Upload size={14} data-id="element-4318" />
                  Carica file
                </button>
                <button onClick={() => setPhotoSourceTab('url')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${photoSourceTab === 'url' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-4319">
                  <Link2 size={14} data-id="element-4320" />
                  URL
                </button>
              </div>

              {/* Upload mode */}
              {photoSourceTab === 'upload' && <div data-id="element-4321">
                  <input ref={photoFileInputRef} type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) readFileAsDataUrl(file, setNewPhotoUrl);
            }} data-id="element-4322" />
                  {!newPhotoUrl ? <div onClick={() => photoFileInputRef.current?.click()} onDragOver={e => {
              e.preventDefault();
              setIsDraggingPhoto(true);
            }} onDragLeave={() => setIsDraggingPhoto(false)} onDrop={e => {
              e.preventDefault();
              setIsDraggingPhoto(false);
              const file = e.dataTransfer.files?.[0];
              if (file) readFileAsDataUrl(file, setNewPhotoUrl);
            }} className={`flex flex-col items-center justify-center gap-3 px-4 py-10 rounded-lg border-2 border-dashed cursor-pointer transition-all ${isDraggingPhoto ? 'border-coral bg-coral/5' : 'border-border hover:border-foreground/30 hover:bg-muted/30'}`} data-id="element-4323">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center" data-id="element-4324">
                        <Upload size={22} className={isDraggingPhoto ? 'text-coral' : 'text-muted-foreground'} data-id="element-4325" />
                      </div>
                      <div className="text-center" data-id="element-4326">
                        <p className="text-sm font-medium text-foreground" data-id="element-4327">
                          Trascina qui un'immagine
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5" data-id="element-4328">
                          oppure clicca per scegliere un file
                        </p>
                      </div>
                      <p className="text-[10px] text-muted-foreground" data-id="element-4329">
                        PNG, JPG, WEBP fino a 10MB
                      </p>
                    </div> : <div className="space-y-2" data-id="element-4330">
                      <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-4331">
                        <img src={newPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" data-id="element-4332" />
                        <button onClick={() => setNewPhotoUrl('')} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm hover:bg-muted transition-colors" aria-label="Rimuovi" data-id="element-4333">
                          <X size={14} className="text-foreground" data-id="element-4334" />
                        </button>
                      </div>
                      <button onClick={() => photoFileInputRef.current?.click()} className="text-xs font-medium text-coral hover:underline" data-id="element-4335">
                        Scegli un'altra immagine
                      </button>
                    </div>}
                </div>}

              {/* URL mode */}
              {photoSourceTab === 'url' && <>
                  <div data-id="element-4336">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4337">
                      URL Immagine
                    </label>
                    <input type="text" value={newPhotoUrl.startsWith('data:') ? '' : newPhotoUrl} onChange={e => setNewPhotoUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-4338" />
                  </div>

                  {newPhotoUrl.trim() && !newPhotoUrl.startsWith('data:') && <div data-id="element-4339">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4340">
                        Anteprima
                      </label>
                      <div className="aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-4341">
                        <img src={newPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).style.display = 'none';
                }} data-id="element-4342" />
                      </div>
                    </div>}
                </>}

              {/* Gallery (always available) */}
              <div className="pt-2 border-t border-border" data-id="element-4343">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 mt-3" data-id="element-4344">
                  Oppure scegli da una galleria
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-4345">
                  {presetPhotos.map(url => <button key={url} onClick={() => setNewPhotoUrl(url)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${newPhotoUrl === url ? 'border-coral ring-2 ring-coral/20' : 'border-border hover:border-foreground/30'}`} data-id="element-4346">
                      <img src={url} alt="" className="w-full h-full object-cover" data-id="element-4347" />
                    </button>)}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border" data-id="element-4348">
              <button onClick={closePhotoModal} className="px-5 py-2.5 bg-white border border-border text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors min-h-[44px]" data-id="element-4349">
                Annulla
              </button>
              <button onClick={handleAddPhoto} disabled={!newPhotoUrl.trim()} className="px-5 py-2.5 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4350">
                Aggiungi foto
              </button>
            </div>
          </div>
        </div>}

      {/* Add New Room Modal */}
      {newRoomModalOpen && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeRoomModal} data-id="element-4351">
          <div className="bg-white rounded-2xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-4352">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-4353">
              <div data-id="element-4354">
                <h2 className="text-base font-bold text-navy" data-id="element-4355">
                  Aggiungi nuova stanza
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-4356">
                  Crea una nuova stanza per organizzare le foto
                </p>
              </div>
              <button onClick={closeRoomModal} className="p-1.5 hover:bg-muted rounded-full transition-colors text-muted-foreground" aria-label="Chiudi" data-id="element-4357">
                <X size={18} data-id="element-4358" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4" data-id="element-4359">
              <div data-id="element-4360">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4361">
                  Nome stanza *
                </label>
                <div className="relative" data-id="element-4362">
                  <DoorOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-4363" />
                  <input type="text" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="Es. Camera ospiti, Bagno principale..." autoFocus className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-4364" />
                </div>
              </div>

              <div data-id="element-4365">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-4366">
                  Prima foto (opzionale)
                </label>

                {/* Source tabs */}
                <div className="flex gap-1 p-1 bg-muted rounded-lg mb-3" data-id="element-4367">
                  <button onClick={() => setRoomSourceTab('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${roomSourceTab === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-4368">
                    <Upload size={14} data-id="element-4369" />
                    Carica file
                  </button>
                  <button onClick={() => setRoomSourceTab('url')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${roomSourceTab === 'url' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-4370">
                    <Link2 size={14} data-id="element-4371" />
                    URL
                  </button>
                </div>

                {/* Upload mode */}
                {roomSourceTab === 'upload' && <>
                    <input ref={roomFileInputRef} type="file" accept="image/*" className="hidden" onChange={e => {
                const file = e.target.files?.[0];
                if (file) readFileAsDataUrl(file, setNewRoomPhotoUrl);
              }} data-id="element-4372" />
                    {!newRoomPhotoUrl ? <div onClick={() => roomFileInputRef.current?.click()} onDragOver={e => {
                e.preventDefault();
                setIsDraggingRoom(true);
              }} onDragLeave={() => setIsDraggingRoom(false)} onDrop={e => {
                e.preventDefault();
                setIsDraggingRoom(false);
                const file = e.dataTransfer.files?.[0];
                if (file) readFileAsDataUrl(file, setNewRoomPhotoUrl);
              }} className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-all ${isDraggingRoom ? 'border-coral bg-coral/5' : 'border-border hover:border-foreground/30 hover:bg-muted/30'}`} data-id="element-4373">
                        <Upload size={20} className={isDraggingRoom ? 'text-coral' : 'text-muted-foreground'} data-id="element-4374" />
                        <p className="text-xs text-foreground text-center" data-id="element-4375">
                          <span className="font-medium" data-id="element-4376">Trascina</span> o{' '}
                          <span className="font-medium" data-id="element-4377">clicca</span> per
                          scegliere
                        </p>
                      </div> : <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-4378">
                        <img src={newRoomPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" data-id="element-4379" />
                        <button onClick={() => setNewRoomPhotoUrl('')} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm hover:bg-muted transition-colors" aria-label="Rimuovi" data-id="element-4380">
                          <X size={14} className="text-foreground" data-id="element-4381" />
                        </button>
                      </div>}
                  </>}

                {/* URL mode */}
                {roomSourceTab === 'url' && <input type="text" value={newRoomPhotoUrl.startsWith('data:') ? '' : newRoomPhotoUrl} onChange={e => setNewRoomPhotoUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-4382" />}
              </div>

              <div data-id="element-4383">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-4384">
                  Galleria suggerimenti
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-4385">
                  {presetPhotos.map(url => <button key={url} onClick={() => setNewRoomPhotoUrl(url)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${newRoomPhotoUrl === url ? 'border-coral ring-2 ring-coral/20' : 'border-border hover:border-foreground/30'}`} data-id="element-4386">
                      <img src={url} alt="" className="w-full h-full object-cover" data-id="element-4387" />
                    </button>)}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2" data-id="element-4388">
                <Info size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-4389" />
                <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-4390">
                  Se non scegli una foto, useremo un'immagine di default. Potrai
                  sostituirla in seguito.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border" data-id="element-4391">
              <button onClick={closeRoomModal} className="px-5 py-2.5 bg-white border border-border text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors min-h-[44px]" data-id="element-4392">
                Annulla
              </button>
              <button onClick={handleAddRoom} disabled={!newRoomName.trim()} className="px-5 py-2.5 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4393">
                Crea stanza
              </button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}